// ─── extractor/index.js ───────────────────────────────────────────────────────
// Public API + main orchestration loop.
//
// Usage:
//   import { extractWidgetsFromHTML } from './extractor';
//   const result = await extractWidgetsFromHTML(htmlString);

import { AI_IMAGE_WIDGET, BUTTON, CONTAINER, SECTION, TEXT } from 'constants/widgetTypes';

import { findMobileMatch, isVisible }             from './utils/dom.js';
import { extractFontFaceMap }                     from './utils/fonts.js';
import { buildSectionPageMap, applyPageWidgetHeights, collectMobileSectionHeights } from './mapping/pageMap.js';
import { buildSectionMap, resolveContentParent, getDomDepth } from './widgets/section.js';
import { createTextWidgetObj, createTextWidgetObjFromChild, resolveTextChildren } from './widgets/text.js';
import { createImageWidgetObj }                   from './widgets/image.js';
import { createVideoWidgetObj }                   from './widgets/video.js';
import { createButtonWidgetObj }                  from './widgets/button.js';
import { createContainerWidgetObj }               from './widgets/container.js';
import { createMenuWidgetObj, createMenuContainerWidgetObj, isNavLogo } from './widgets/menu.js';
import { getAbsoluteRect }                        from './utils/dom.js';

// ─── HTML pre-processor ───────────────────────────────────────────────────────
// Strips <script> tags and makes hidden elements visible so layout is correct.
function preprocessHTML(html) {
    html = html.replace(/<script[\s\S]*?<\/script>/gi, '');
    html = html.replace(
        /style="([^"]*)"/gi,
        (_, sv) => `style="${sv
            .replace(/\bopacity\s*:\s*0\b/g,          'opacity:1')
            .replace(/\bvisibility\s*:\s*hidden\b/g,  'visibility:visible')}"`,
    );
    html = html.replace(
        /(<style[^>]*>)([\s\S]*?)(<\/style>)/gi,
        (_, open, css, close) => open + css
            .replace(/opacity\s*:\s*0(?![.\d])/g, 'opacity:1')
            .replace(/visibility\s*:\s*hidden/g,  'visibility:visible') + close,
    );
    return html;
}

// ─── Hidden iframe helpers ────────────────────────────────────────────────────
function createHiddenIframe(html, width, vid) {
    const iframe = document.createElement('iframe');
    iframe.style.cssText = `width:${width}px;height:100vh;position:absolute;left:-9999px;top:-9999px;visibility:hidden;border:none;`;
    iframe.id = `html-import-${vid}`;
    document.body.appendChild(iframe);

    const mod = html
        .replace('<head>', `<head><meta name="viewport" content="width=${width}, initial-scale=1">`)
        .replace('</body>', `<scr` + `ipt>
            window.addEventListener('load', function () {
                setTimeout(function () {
                    window.parent.postMessage('IFRAME_READY_${vid}', '*');
                }, 800);
            });
        </scr` + `ipt></body>`);

    iframe.contentDocument.open();
    iframe.contentDocument.write(mod);
    iframe.contentDocument.close();
    return iframe;
}

function waitForIframe(vid, timeout = 6000) {
    return new Promise((resolve) => {
        const handler = (e) => {
            if (e.data === `IFRAME_READY_${vid}`) {
                window.removeEventListener('message', handler);
                resolve();
            }
        };
        window.addEventListener('message', handler);
        setTimeout(resolve, timeout);
    });
}

// ─── Core extraction ──────────────────────────────────────────────────────────
function extractAllWidgets(desktopDoc, mobileDoc) {
    const orderRef = { value: 1 };
    const pureListWrappers = new Set();

    // ── Font map ──────────────────────────────────────────────────────────────
    const { fontFamilyMap, fontIdMap } = extractFontFaceMap(desktopDoc);

    // ── Page → section mapping ────────────────────────────────────────────────
    // Creates one fresh PAGE widget per <section>/<footer> from the DOM alone.
    // No pageWidgets input needed — everything is self-contained.
    const { sectionPageMap, allNewPageWidgets, narrowSectionEls, firstPageUid } = buildSectionPageMap(desktopDoc);

    const fallbackUid = firstPageUid || `widget_fallback`;

    // ── Section + column widgets ──────────────────────────────────────────────
    const { sectionWidgets, columnWidgets, sectionMap } = buildSectionMap(
        desktopDoc, mobileDoc, sectionPageMap, fallbackUid, orderRef, narrowSectionEls,
    );

    const contentWidgets = [];

    // ── Skip sets ─────────────────────────────────────────────────────────────
    // 1. [data-widget="text"] nodes directly inside a button (avoid double-emit)
    const textsInsideButtons = new Set();
    desktopDoc.querySelectorAll('[data-widget="button"]').forEach((b) =>
        b.querySelectorAll('[data-widget="text"]').forEach((t) => textsInsideButtons.add(t)),
    );

    // 2. Everything inside [data-widget="menu-container"] — populated during menu pass
    const menuContainerChildEls = new Set();

    // 3. Nav-link <a> elements and hamburger buttons (identified during menu scan)
    const navLinkEls   = new Set();
    const hamburgerEls = new Set();

    // ── Hamburger / nav-link scanner ──────────────────────────────────────────
    // Runs before the menu pass so skip sets are ready.
    const menuEls = Array.from(desktopDoc.querySelectorAll('[data-widget="menu"]'));

    const isHamburger = (el) =>
        !!el.querySelector('svg') ||
        (el.tagName === 'BUTTON' && el.textContent.trim().length <= 3) ||
        /menu|open|close|toggle|nav/.test((el.getAttribute('aria-label') || '').toLowerCase());

    menuEls.forEach((menuEl) => {
        const container = menuEl.querySelector('[data-widget="menu-container"]') || menuEl;
        Array.from(container.querySelectorAll('a, [data-widget="button"]')).forEach((el) => {
            // nav-logo elements are handled separately — they become standalone
            // text/button widgets parented to the menu uid, so skip them here.
            if (isNavLogo(el)) return;
            const text     = el.textContent.trim();
            const isHidden = window.getComputedStyle(el).display === 'none';
            if (isHamburger(el) || isHidden) { hamburgerEls.add(el); return; }
            if (el.getAttribute('href') || text.length > 3) navLinkEls.add(el);
        });
    });

    // ── Ancestry helper ───────────────────────────────────────────────────────
    const menuContainerDOMEls    = Array.from(desktopDoc.querySelectorAll('[data-widget="menu-container"]'));
    const isInsideMenuContainer  = (el) => menuContainerDOMEls.some((c) => c !== el && c.contains(el));

    // ── Menu uid registry (used by resolveWithMenu) ───────────────────────────
    const menuUidMap = new Map(); // menuEl → { menuUid, menuRect, mobileMenuRect }

    // ── PASS: Menu ────────────────────────────────────────────────────────────
    menuEls.forEach((el) => {
        if (!isVisible(el)) return;
        const pm  = sectionPageMap.get(el);
        const pid = pm?.pageUid ?? fallbackUid;
        const { offsetRect, mobileOffsetRect } = resolveContentParent(el, sectionMap, [], fallbackUid);
        const mob = findMobileMatch(el, mobileDoc, '[data-widget="menu"]', desktopDoc);

        const { menuWidget, menuUid } = createMenuWidgetObj({
            el, mobileEl: mob, parentUid: pid, offsetRect, mobileOffsetRect, orderRef,
        });
        contentWidgets.push(menuWidget);

        const menuRect       = getAbsoluteRect(el);
        const mobileMenuRect = mob ? getAbsoluteRect(mob) : { top: menuRect.top, left: 0, height: menuRect.height };
        menuUidMap.set(el, { menuUid, menuRect, mobileMenuRect });

        const cEl = el.querySelector('[data-widget="menu-container"]');
        if (cEl) {
            const mCEl = mob ? mob.querySelector('[data-widget="menu-container"]') : null;
            contentWidgets.push(createMenuContainerWidgetObj({
                el: cEl, mobileEl: mCEl, menuUid,
                offsetRect: menuRect, mobileOffsetRect: mobileMenuRect,
                orderRef, menuWidget, menuContainerChildEls, fontFamilyMap, fontIdMap,
            }));
        }
    });

    const containerMap = [];

    // resolveWithMenu: if an element is inside a menu, parent it to the menu uid
    const resolveWithMenu = (domEl) => {
        for (const [menuEl, { menuUid, menuRect, mobileMenuRect }] of menuUidMap) {
            if (menuEl.contains(domEl))
                return { parentUid: menuUid, offsetRect: menuRect, mobileOffsetRect: mobileMenuRect };
        }
        return resolveContentParent(domEl, sectionMap, containerMap, fallbackUid);
    };

    // ── PASS: Container ───────────────────────────────────────────────────────
    // Process outer containers before inner (shallow-to-deep) so parent uids exist.
    const containerEls = Array.from(desktopDoc.querySelectorAll('[data-widget="container"]'))
        .filter((el) => isVisible(el))
        .filter((el) => !menuContainerChildEls.has(el))
        .filter((el) => !(isInsideMenuContainer(el) && !isNavLogo(el)))
        .sort((a, b) => getDomDepth(a) - getDomDepth(b));

    containerEls.forEach((el) => {
        const { parentUid, offsetRect, mobileOffsetRect } =
            resolveContentParent(el, sectionMap, containerMap, fallbackUid);
        const mob = findMobileMatch(el, mobileDoc, '[data-widget="container"]', desktopDoc);
        const widget = createContainerWidgetObj({
            el, mobileEl: mob, parentUid, offsetRect, mobileOffsetRect, orderRef, doc: desktopDoc,
        });
        contentWidgets.push(widget);

        const rect       = getAbsoluteRect(el);
        const mobileRect = mob ? getAbsoluteRect(mob) : rect;
        containerMap.push({
            containerElement: el,
            containerUid:     widget.uid,
            rect,
            mobileRect,
            depth:            getDomDepth(el),
        });
    });

    // ── PASS: Image ───────────────────────────────────────────────────────────
    desktopDoc.querySelectorAll('[data-widget="image"]').forEach((el) => {
        if (!isVisible(el)) return;
        const { parentUid, offsetRect, mobileOffsetRect } = resolveWithMenu(el);
        const mob = findMobileMatch(el, mobileDoc, '[data-widget="image"]', desktopDoc);
        contentWidgets.push(createImageWidgetObj({
            el, mobileEl: mob, parentUid, offsetRect, mobileOffsetRect, orderRef,
            prompt:      el.getAttribute('data-prompt')       || null,
            aspectRatio: el.getAttribute('data-aspect-ratio') || '16:9',
        }));
    });

    // ── PASS: Text ────────────────────────────────────────────────────────────
    // Skip rules:
    //   • not visible
    //   • in navLinkEls (nav links handled by MenuContainer content)
    //   • text node directly inside a button
    //   • in menuContainerChildEls skip set (nav-logo is NOT in this set)
    //   • inside menu-container by DOM ancestry, UNLESS it is a nav-logo
    //   • nested list (not the root ul/ol)
    //   • <li data-widget="text"> whose parent <ul>/<ol> is itself being emitted
    //     as a list widget — the list builder in text.js already includes all
    //     <li> text, so emitting <li> separately creates duplicates.
    desktopDoc.querySelectorAll('[data-widget="text"]').forEach((el) => {
      const innerList = el.querySelector("ul, ol");
      if (!innerList) return;
      const clone = el.cloneNode(true);
      clone.querySelectorAll("ul, ol").forEach((l) => l.remove());
      if (!clone.textContent.trim()) {
        pureListWrappers.add(el); // mark wrapper as skippable
      }
    });

    desktopDoc
        .querySelectorAll('[data-widget="text"], ul, ol')
        .forEach((el) => {
        if (!isVisible(el)) return;
        if (navLinkEls.has(el)) return;
        if (textsInsideButtons.has(el)) return;
        if (menuContainerChildEls.has(el)) return;
        if (isInsideMenuContainer(el) && !isNavLogo(el)) return;

            const tag = el.tagName.toLowerCase();
 
            // Skip nested lists
            if ((tag === 'ul' || tag === 'ol') && el.closest('ul, ol') !== el) return;
 
            // Skip <li data-widget="text">
            if (tag === 'li') return;
 
            if (el.hasAttribute?.('data-widget') && el.getAttribute('data-widget') === 'text') {
                // [data-widget="text"] nested inside a ul/ol → skip
                if (el.closest('ul, ol')) return;
                // Pure list wrapper → skip; the inner ul/ol emits itself below
                if (pureListWrappers.has(el)) return;
                // Mixed (text + list) → keep; text.js handles via isMixed

                // Nested [data-widget="text"] inside another [data-widget="text"] → skip.
                // e.g. <div data-widget="text"><a data-widget="text">Label</a></div>
                // The outer wrapper is already emitted as a widget; the inner is a duplicate.

                const outerTextWidget = el.parentElement?.closest('[data-widget="text"]');
                if (outerTextWidget) return;
            }
 
            if (tag === 'ul' || tag === 'ol') {
                // Bare ul/ol inside a mixed [data-widget="text"] → skip (parent emits it)
                const parentTextWidget = el.closest('[data-widget="text"]');
                if (parentTextWidget && !pureListWrappers.has(parentTextWidget)) return;
                // Bare ul/ol inside a pure-list wrapper → allow (wrapper was skipped)
            }
 
            const { parentUid, offsetRect, mobileOffsetRect } = resolveWithMenu(el);
            const mob = findMobileMatch(el, mobileDoc, '[data-widget="text"], ul, ol', desktopDoc);
 
            // ── Multi-child split: <div data-widget="text"><h3>…</h3><p>…</p></div>
            // When a wrapper has 2+ heading/paragraph children (no lists), emit one
            // independent TEXT widget per child so each gets correct styles + rect.
            const splitChildren = resolveTextChildren(el);
            if (splitChildren) {
                splitChildren.forEach((childEl, childIndex) => {
                    contentWidgets.push(createTextWidgetObjFromChild({
                        wrapperEl:       el,
                        mobileWrapperEl: mob,
                        childEl,
                        childIndex,
                        parentUid, offsetRect, mobileOffsetRect, orderRef, fontFamilyMap, fontIdMap,
                    }));
                });
                return; // wrapper replaced entirely by per-child widgets
            }
 
            // Default: single widget for this element
            contentWidgets.push(createTextWidgetObj({
                el, mobileEl: mob, parentUid, offsetRect, mobileOffsetRect, orderRef, fontFamilyMap, fontIdMap,
            }));
        });

    // ── PASS: Video ───────────────────────────────────────────────────────────
    desktopDoc.querySelectorAll('[data-widget="video"]').forEach((el) => {
        if (!isVisible(el)) return;
        const { parentUid, offsetRect, mobileOffsetRect } = resolveWithMenu(el);
        const mob = findMobileMatch(el, mobileDoc, '[data-widget="video"]', desktopDoc);
        contentWidgets.push(createVideoWidgetObj({
            el, mobileEl: mob, parentUid, offsetRect, mobileOffsetRect, orderRef,
        }));
    });

    // ── PASS: Button ──────────────────────────────────────────────────────────
    // Skip: nav links, hamburgers, menu-container children (nav-logo is NOT skipped —
    // it passes through and gets the menu uid as parent via resolveWithMenu)
    desktopDoc.querySelectorAll('[data-widget="button"]').forEach((el) => {
        if (!isVisible(el))                              return;
        if (navLinkEls.has(el))                          return;
        if (hamburgerEls.has(el))                        return;
        if (menuContainerChildEls.has(el))               return;
        if (isInsideMenuContainer(el) && !isNavLogo(el)) return;

        const { parentUid, offsetRect, mobileOffsetRect } = resolveWithMenu(el);
        const mob = findMobileMatch(el, mobileDoc, '[data-widget="button"]', desktopDoc);
        contentWidgets.push(createButtonWidgetObj({
            el, mobileEl: mob, parentUid, offsetRect, mobileOffsetRect, orderRef, fontFamilyMap, fontIdMap,
        }));
    });

    // ── Page widget height accumulation ───────────────────────────────────────
    const dMenuEl       = desktopDoc.querySelector('[data-widget="menu"]');
    const mMenuEl       = mobileDoc.querySelector('[data-widget="menu"]');
    const mobileHeights = collectMobileSectionHeights(mobileDoc, sectionPageMap);
    applyPageWidgetHeights(allNewPageWidgets, sectionPageMap, dMenuEl, mMenuEl, mobileHeights, firstPageUid);
    const newOrderedPageWidgets = [...allNewPageWidgets].reverse();

    return {
        widgets:            [...sectionWidgets, ...columnWidgets, ...contentWidgets].sort((a, b) => a.order - b.order),
        updatedPageWidgets: newOrderedPageWidgets,
    };
}

// ─── Public API ───────────────────────────────────────────────────────────────
/**
 * Extracts CleverStory widgets from an HTML string.
 * One PAGE widget is auto-created per <section>/<footer> found in the HTML.
 * No page widgets need to be supplied — everything is derived from the DOM.
 *
 * @param {string} htmlContent   Raw HTML to parse
 *
 * @returns {Promise<{
 *   widgets:            Widget[],        // all content widgets sorted by order
 *   updatedPageWidgets: PageWidget[],    // one per section, with computed heights
 *   stats:              Object,
 * }>}
 */
export async function extractWidgetsFromHTML(htmlContent) {
    const dId = `D_${Date.now()}`;
    const mId = `M_${Date.now()}`;

    const cleanedHtml   = preprocessHTML(htmlContent);
    const desktopIframe = createHiddenIframe(cleanedHtml, 1200, dId);
    const mobileIframe  = createHiddenIframe(cleanedHtml, 375,  mId);

    await Promise.all([waitForIframe(dId), waitForIframe(mId)]);

    const desktopDoc = desktopIframe.contentDocument;
    const mobileDoc  = mobileIframe.contentDocument;

    const { widgets, updatedPageWidgets } =
        extractAllWidgets(desktopDoc, mobileDoc);

    document.body.removeChild(desktopIframe);
    document.body.removeChild(mobileIframe);

    return {
        widgets,
        updatedPageWidgets,
        stats: {
            totalWidgets:  widgets.length,
            totalSections: widgets.filter((w) => w.type === SECTION).length,
            totalImages:   widgets.filter((w) => w.type === AI_IMAGE_WIDGET).length,
            totalTexts:    widgets.filter((w) => w.type === TEXT).length,
            totalButtons:  widgets.filter((w) => w.type === BUTTON).length,
            totalContainers: widgets.filter((w) => w.type === CONTAINER).length,
        },
    };
}
